/**
 * AgentOsDemoGdprWatch.jsx — public sandbox for the GDPR + A11y Watch Agent.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Sparkles, ArrowRight, Loader2, Lock, ShieldCheck, AlertTriangle,
} from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const LANGS = [
  { id: "sk", name: "Slovenčina" }, { id: "cs", name: "Čeština" },
  { id: "en", name: "English" }, { id: "de", name: "Deutsch" },
];

const EXAMPLES = [
  { label: "Tvoj e-shop", url: "" },
  { label: "google.com", url: "google.com" },
  { label: "github.com", url: "github.com" },
];

const SEV = {
  critical: { color: "bg-rose-950/50 text-rose-200 border-rose-500/50", label: "CRITICAL" },
  high:     { color: "bg-rose-900/30 text-rose-300 border-rose-700/40", label: "HIGH" },
  medium:   { color: "bg-amber-900/30 text-amber-300 border-amber-700/40", label: "MEDIUM" },
  low:      { color: "bg-emerald-900/30 text-emerald-300 border-emerald-700/40", label: "LOW" },
};

const GRADE_COLOR = {
  A: "text-emerald-400", B: "text-lime-400", C: "text-amber-400",
  D: "text-orange-400", E: "text-rose-400", F: "text-rose-500",
};

export default function AgentOsDemoGdprWatch() {
  const [form, setForm] = useState({ url: "", lang: "sk" });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/gdpr-watch`, {
        url: form.url.trim(), lang: form.lang,
      });
      setResult(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "object" ? d : { message: d || "Skús to ešte raz." } });
      return;
    }
    setStatus({ loading: false, error: null });
  };

  const Left = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-black border border-gray-800">
      <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> Compliance scan
      </h2>

      <div className="flex flex-wrap gap-2">
        <span className="text-xs uppercase tracking-widest text-gray-500 self-center mr-2">Príklad:</span>
        {EXAMPLES.filter((e) => e.url).map((ex, i) => (
          <button key={i} type="button" onClick={() => { setForm((f) => ({ ...f, url: ex.url })); setResult(null); }}
            data-testid={`agent-os-demo-gdpr-example-${i}`}
            className="px-3 py-1 text-xs bg-[#0a0a0a] border border-gray-800 hover:border-emerald-700/60 text-gray-300 hover:text-emerald-300">
            {ex.label}
          </button>
        ))}
      </div>

      <input required type="text" placeholder="URL e-shopu (napr. mojshop.sk) *"
        value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
        data-testid="agent-os-demo-gdpr-url"
        className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600" />

      <div>
        <label className="text-xs text-gray-500 uppercase tracking-widest mb-1 block">Jazyk reportu</label>
        <select value={form.lang} onChange={(e) => setForm({ ...form, lang: e.target.value })}
          data-testid="agent-os-demo-gdpr-lang"
          className="w-full px-3 py-2 text-sm bg-[#0a0a0a] border border-gray-800 focus:border-emerald-500 outline-none text-white">
          {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      <div className="p-3 bg-amber-950/20 border border-amber-700/30 text-xs text-amber-200">
        <strong>Čo kontrolujeme:</strong> cookie consent, privacy policy link, alt texty obrázkov,
        labels na formulároch, HTML lang atribút, viewport meta + 3-5 AI-detected subtle issues
        (kontrast, ARIA, dark patterns). <strong>WCAG 2.1 AA</strong> + <strong>GDPR</strong>.
      </div>

      <button type="submit" disabled={status.loading || !form.url.trim()}
        data-testid="agent-os-demo-gdpr-submit"
        className="w-full px-5 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm flex items-center justify-center gap-2">
        {status.loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Skenujem…</>
          : <>Spusti compliance scan <Sparkles className="w-4 h-4" /></>}
      </button>
    </form>
  );

  const grade = result?.output?.overall_grade;
  const score = result?.output?.compliance_score;

  const Right = (
    <div className="space-y-4">
      <div className="p-6 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 min-h-[300px]">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-300 mb-3">Compliance Report</h2>

        {!result && !status.loading && !status.error && (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="agent-os-demo-gdpr-empty">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-gray-700" /> Zadaj URL a klikni „Spusti scan"
          </div>
        )}
        {status.loading && (
          <div className="text-center py-12 text-emerald-300 text-sm" data-testid="agent-os-demo-gdpr-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" /> Sťahujem HTML + analyzujem…
          </div>
        )}
        {status.error && (
          <div className="p-4 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm" data-testid="agent-os-demo-gdpr-error">
            <div className="font-bold mb-1">{status.error?.message || "Niečo zlyhalo"}</div>
            {status.error?.cta_url && (
              <a href={status.error.cta_url} className="inline-flex items-center gap-1 mt-2 text-emerald-300 underline text-xs font-bold">
                Registruj sa zadarmo <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {result?.output && (
          <div data-testid="agent-os-demo-gdpr-result" className="space-y-4">
            <div className="flex items-center gap-6 p-4 bg-black/50 border border-emerald-900/40">
              <div className="text-center">
                <div className={`text-6xl font-black tabular-nums ${GRADE_COLOR[grade] || "text-gray-400"}`} data-testid="agent-os-demo-gdpr-grade">
                  {grade}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Známka</div>
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-gray-400 mb-1">Compliance score</div>
                <div className="text-3xl font-black tabular-nums text-white">
                  {score}<span className="text-base text-gray-500">/100</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{result.output.scanned_url}</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">
                Nájdené problémy ({(result.output.issues || []).length})
              </div>
              <div className="space-y-2">
                {(result.output.issues || []).map((iss, i) => {
                  const s = SEV[iss.severity] || SEV.medium;
                  return (
                    <div key={i} data-testid={`agent-os-demo-gdpr-issue-${i}`}
                      className="p-3 bg-black/50 border border-gray-800">
                      <div className="flex items-start gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border font-bold ${s.color}`}>
                          {s.label}
                        </span>
                        <div className="text-sm font-bold text-white flex-1">{iss.title}</div>
                      </div>
                      <p className="text-xs text-gray-400 ml-1">{iss.fix}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {(result.output.recommendations || []).length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold mb-2">
                  AI Recommendations
                </div>
                <ul className="space-y-1">
                  {result.output.recommendations.map((rec, i) => (
                    <li key={i} className="text-xs text-gray-300 flex gap-2 items-start">
                      <Sparkles className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{result.latency_ms} ms</span>
              {result.used_fallback && <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Heuristics only</span>}
              <span className="px-2 py-1 bg-gray-900 text-gray-400">Zostáva dnes: {result.remaining_today}</span>
            </div>
            <a href="/agent-os/onboard" data-testid="agent-os-demo-gdpr-cta"
              className="block px-4 py-3 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest text-center flex items-center justify-center gap-2">
              Aktivovať denný compliance scan (€49/mes) <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      <div className="p-4 bg-black border border-gray-800 text-xs text-gray-500 flex gap-3 items-start">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
        <div>Demo má limit 3 skeny / IP / 24h. <a href="/privacy" className="text-emerald-400 underline ml-1">Privacy →</a></div>
      </div>
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-os-demo-gdpr-watch-page"
      h1={<>GDPR + A11y <span className="text-emerald-400">Watch</span></>}
      lead="Zadaj URL → scan GDPR (cookies, privacy) + WCAG 2.1 AA (alt texty, labels, lang, kontrast). Známka A-F + akčný checklist. 3 skeny zadarmo."
      left={Left}
      right={Right}
    />
  );
}
